import { Walk, Waypoint } from '../types/walk'

class WalkService {
  private baseUrl = '/api/walks'

  private getHeaders(includeAuth: boolean = false) {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }
    
    if (includeAuth) {
      const adminToken = localStorage.getItem('adminToken')
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`
      }
    }
    
    return headers
  }

  async getAllWalks(): Promise<Walk[]> {
    // Try with admin auth first, fall back to public
    const adminToken = localStorage.getItem('adminToken')
    const headers = adminToken ? this.getHeaders(true) : {}
    
    const response = await fetch(this.baseUrl, { headers })
    if (!response.ok) {
      throw new Error(`Failed to fetch walks: ${response.statusText}`)
    }
    return response.json()
  }

  async createWalk(walk: Omit<Walk, 'id' | 'created_date' | 'last_updated'>): Promise<Walk> {
    // Extract waypoints from walk data if present
    const { waypoints, ...walkData } = walk
    
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(walkData)
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.details || error.error || `Failed to create walk: ${response.statusText}`)
    }
    
    const createdWalk = await response.json()
    
    // Add waypoints if provided
    if (waypoints && waypoints.length > 0) {
      await this.updateWaypoints(createdWalk.id, waypoints)
      createdWalk.waypoints = waypoints
    }
    
    return createdWalk
  }

  async updateWalk(id: string, updates: Partial<Walk>): Promise<Walk> {
    // Extract waypoints from updates if present
    const { waypoints, ...walkUpdates } = updates
    
    // Update walk metadata
    const response = await fetch(`${this.baseUrl}?id=${id}`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify(walkUpdates)
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.details || error.error || `Failed to update walk: ${response.statusText}`)
    }
    
    const updatedWalk = await response.json()
    
    // Update waypoints separately if provided
    if (waypoints !== undefined) {
      await this.updateWaypoints(id, waypoints)
      updatedWalk.waypoints = waypoints
    }
    
    return updatedWalk
  }
  
  async getWaypoints(walkId: string): Promise<Waypoint[]> {
    const response = await fetch(`/api/waypoints?walkId=${walkId}`, {
      method: 'GET',
      headers: this.getHeaders(false)
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.details || error.error || `Failed to fetch waypoints: ${response.statusText}`)
    }
    
    return response.json()
  }
  
  async updateWaypoints(walkId: string, waypoints: Waypoint[]): Promise<Waypoint[]> {
    const response = await fetch(`/api/waypoints?walkId=${walkId}`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(waypoints)
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      console.error('Failed to update waypoints:', error)
      throw new Error(error.details || error.error || `Failed to update waypoints: ${response.statusText}`)
    }
    
    return response.json()
  }

  async deleteWalk(id: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}?id=${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(true)
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.details || error.error || `Failed to delete walk: ${response.statusText}`)
    }
  }

  async getAllWalkActivities(): Promise<Array<{ walk_id: string; walk_name: string; strava_activity_id: string; date: string }>> {
    const walks = await this.getAllWalks()
    const allActivities: Array<{ walk_id: string; walk_name: string; strava_activity_id: string; date: string }> = []
    
    for (const walk of walks) {
      try {
        const response = await fetch(`/api/walk-activities?walkId=${walk.id}`)
        if (response.ok) {
          const data = await response.json()
          
          // The edge function returns days/activities which are grouped by date
          // Each day might have multiple activities nested inside
          if (data.activities && Array.isArray(data.activities)) {
            for (const dayGroup of data.activities) {
              // Check if this day has nested activities (multiple activities on same day)
              if (dayGroup.activities && Array.isArray(dayGroup.activities)) {
                for (const activity of dayGroup.activities) {
                  if (activity.strava_activity_id) {
                    allActivities.push({
                      walk_id: walk.id,
                      walk_name: walk.name,
                      strava_activity_id: activity.strava_activity_id,
                      date: activity.date
                    })
                  }
                }
              } else if (dayGroup.strava_activity_id) {
                // Single activity for this day
                allActivities.push({
                  walk_id: walk.id,
                  walk_name: walk.name,
                  strava_activity_id: dayGroup.strava_activity_id,
                  date: dayGroup.date
                })
              }
            }
          }
        }
      } catch (error) {
        console.error(`Failed to fetch activities for walk ${walk.id}:`, error)
      }
    }
    
    return allActivities
  }
}

export const walkService = new WalkService()